import { useCallback, useEffect, useRef, useState } from 'react';
import { FlappyEngine, bestScore } from './game/engine';
import { EXTRA_LIFE, type Phase } from './game/constants';
import { EtooLogo } from './EtooLogo';
import { reportGameOver, reportPlay } from './beacon';
import {
  ensureIdentity,
  fetchLeaderboards,
  getOrCreateLocalName,
  loadLocalScores,
  localLeaderboards,
  monthKeyLocal,
  monthLabel,
  recordLocalScore,
  submitScore,
  type Leaderboards,
  type PlayerIdentity,
  type ScoreEntry,
} from './game/leaderboard';

/** Golden coin drawn as SVG — the 🪙 emoji renders grey on systems
 *  whose fonts predate Unicode 14, so we never rely on it. */
function CoinIcon({ className = '' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <circle cx="12" cy="12" r="10" fill="#fbbf24" stroke="#b45309" strokeWidth="2" />
      <circle cx="12" cy="12" r="5.5" fill="none" stroke="#fef3c7" strokeWidth="2" />
    </svg>
  );
}

/** One top-10 board (global or monthly). Highlights the current player's row. */
function BoardTable({
  title,
  entries,
  highlight,
  loading,
}: {
  title: string;
  entries: ScoreEntry[];
  highlight: string | null;
  loading: boolean;
}) {
  const me = (highlight ?? '').toLowerCase();
  return (
    <div className="rounded-xl bg-[#1e3a5f]/5 p-2">
      <p className="text-[10px] font-bold uppercase tracking-widest text-[#1e3a5f]/60">{title}</p>
      {loading ? (
        <p className="mt-2 text-[10px] font-medium text-[#1e3a5f]/50">Saving score…</p>
      ) : entries.length === 0 ? (
        <p className="mt-2 text-[10px] font-medium text-[#1e3a5f]/50">No scores yet — be the first!</p>
      ) : (
        <ol className="mt-1.5 space-y-0.5">
          {entries.map((e, i) => {
            const isMe = me !== '' && e.name.toLowerCase() === me;
            return (
              <li
                key={`${e.name}-${i}`}
                className={[
                  'flex items-center gap-1 rounded px-1 py-px text-[10px] font-semibold',
                  isMe ? 'bg-amber-200/80 text-[#1e3a5f]' : 'text-[#1e3a5f]/80',
                ].join(' ')}
              >
                <span className="w-3 shrink-0 text-right tabular-nums text-[#1e3a5f]/40">{i + 1}</span>
                <span className="min-w-0 flex-1 truncate">{e.name}</span>
                <span className="shrink-0 tabular-nums">{e.score}</span>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

export default function App() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const engineRef = useRef<FlappyEngine | null>(null);
  const runStartedAt = useRef(0);

  const [phase, setPhase] = useState<Phase>('ready');
  const [score, setScore] = useState(0);
  const [best, setBest] = useState(bestScore.load());
  const [isNewBest, setIsNewBest] = useState(false);
  const [coins, setCoins] = useState(0);
  const [nextCost, setNextCost] = useState<number>(EXTRA_LIFE.baseCost);
  const [reviveFlash, setReviveFlash] = useState(false);
  const [identity, setIdentity] = useState<PlayerIdentity | null>(null);
  const [boards, setBoards] = useState<Leaderboards | null>(null);
  const [boardsLoading, setBoardsLoading] = useState(false);
  const [boardsNote, setBoardsNote] = useState<string | null>(null);
  const identityRef = useRef<PlayerIdentity | null>(null);

  // Who is playing: claimed username when logged in, otherwise the temporary
  // guest label the auth service hands out. Resolved once so the game-over
  // submit never waits on it mid-death.
  useEffect(() => {
    let cancelled = false;
    void ensureIdentity().then((id) => {
      if (cancelled) return;
      identityRef.current = id;
      setIdentity(id);
    });
    // Warm the boards so a returning player sees them instantly on first death.
    void fetchLeaderboards().then((b) => {
      if (!cancelled && b) setBoards(b);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const persistAndLoadBoards = useCallback(
    async (finalScore: number, durationMs: number) => {
      setBoardsLoading(true);
      setBoardsNote(null);
      try {
        const me = identityRef.current ?? (await ensureIdentity());
        if (me) {
          identityRef.current = me;
          setIdentity(me);
        }
        const displayName = me?.name ?? getOrCreateLocalName();
        const displayKind = me?.kind ?? 'guest';

        if (finalScore > 0 && me?.name) {
          const submitted = await submitScore(finalScore, durationMs);
          if (submitted.ok && submitted.leaderboards) {
            setBoards(submitted.leaderboards);
            recordLocalScore(displayName, displayKind, finalScore);
            return;
          }
          if (submitted.error === 'needs_username') {
            setBoardsNote('Claim a username on etoo.in to enter the leaderboard.');
          } else if (submitted.error === 'unavailable' || submitted.error === 'no_session') {
            // Fall through to the explicit fetch, then local.
          }
        }

        const remote = await fetchLeaderboards();
        if (remote) {
          setBoards(remote);
          if (finalScore > 0) recordLocalScore(displayName, displayKind, finalScore);
          if (!me) setBoardsNote('Offline board — scores save on this device only.');
          return;
        }

        // Auth unreachable: keep a device-local journal so both boards still work.
        if (finalScore > 0) recordLocalScore(displayName, displayKind, finalScore);
        else loadLocalScores();
        setBoards(localLeaderboards());
        setBoardsNote('Offline board — scores save on this device only.');
      } finally {
        setBoardsLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const engine = new FlappyEngine(ctx, {
      onScore: (s) => setScore(s),
      onCoins: (c, n) => {
        setCoins(c);
        setNextCost(n);
      },
      onRevive: () => {
        setReviveFlash(true);
        window.setTimeout(() => setReviveFlash(false), 1600);
      },
      onPhase: (p) => {
        setPhase(p);
        if (p === 'ready') {
          // Wallet persists across runs — sync the HUD with it.
          setCoins(engineRef.current?.coins ?? 0);
          setNextCost(engineRef.current?.nextLifeCost ?? EXTRA_LIFE.baseCost);
        }
        if (p === 'playing') {
          // Fires once per run: a revive keeps the phase at 'playing', so
          // spending coins to carry on does not read as a second game.
          runStartedAt.current = performance.now();
          reportPlay();
        }
        if (p === 'gameover') {
          const durationMs = performance.now() - runStartedAt.current;
          reportGameOver(durationMs);
          // Save the best score on ANY death (pipe or ground).
          const s = engineRef.current?.score ?? 0;
          if (s > 0) {
            const prev = bestScore.load();
            if (s > prev) {
              bestScore.save(s);
              setBest(s);
              setIsNewBest(true);
            }
          }
          // Record this run in the SQLite-backed leaderboards (or the
          // device-local fallback) and refresh both boards for the overlay.
          void persistAndLoadBoards(s, durationMs);
        }
      },
    });
    engineRef.current = engine;

    // Sync the HUD with the persisted coin wallet.
    setCoins(engine.coins);
    setNextCost(engine.nextLifeCost);

    // Size the canvas to fill the viewport (at device-pixel resolution for
    // crisp rendering) and tell the engine the logical size.
    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = window.innerWidth;
      const h = window.innerHeight;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      engine.resize(w, h);
    };
    resize();
    window.addEventListener('resize', resize);

    engine.start();

    return () => {
      window.removeEventListener('resize', resize);
      engine.destroy();
      engineRef.current = null;
    };
  }, [persistAndLoadBoards]);

  const press = useCallback(() => {
    engineRef.current?.press();
  }, []);

  // Keyboard
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.code === 'Space' || e.code === 'ArrowUp') {
        e.preventDefault();
        engineRef.current?.press();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Coins still needed for the next extra life (0 = one is affordable now).
  const coinsToLife = Math.max(0, nextCost - coins);

  // Reset "new best" flag when a new run starts
  useEffect(() => {
    if (phase === 'ready') setIsNewBest(false);
  }, [phase]);

  return (
    <div className="fixed inset-0 overflow-hidden bg-[#4ec0ca]">
      <canvas
        ref={canvasRef}
        onClick={press}
        className="absolute inset-0 block h-full w-full cursor-pointer touch-manipulation select-none"
        aria-label="Flappy Bird game. Click, tap, or press space to flap."
      />

      {/* In-flight score */}
      {phase === 'playing' && (
        <div className="pointer-events-none absolute inset-x-0 top-[4vh] flex justify-center">
          <span className="font-display text-4xl text-white [text-shadow:0_3px_0_rgba(0,0,0,0.25)]">
            {score}
          </span>
        </div>
      )}

      {/* Extra-life HUD — top-right, clear of the etoo logo */}
      {(phase === 'playing' || phase === 'ready') && (
        <div className="pointer-events-none absolute right-3 top-3">
          <span
            className={[
              'rounded-full px-4 py-2 font-display text-lg backdrop-blur-sm [text-shadow:0_2px_0_rgba(0,0,0,0.25)]',
              coinsToLife > 0
                ? 'bg-[rgba(30,58,95,0.55)] text-white'
                : 'bg-amber-400/90 text-[#1e3a5f] [text-shadow:none]',
            ].join(' ')}
          >
            {coinsToLife > 0 ? (
              <span className="inline-flex items-center gap-1.5">
                <CoinIcon className="h-5 w-5" />
                {coinsToLife} to extra life
              </span>
            ) : (
              '🛡️ Extra life ready!'
            )}
          </span>
        </div>
      )}

      {/* Extra-life revive banner */}
      {reviveFlash && phase === 'playing' && (
        <div className="pointer-events-none absolute inset-x-0 top-[18vh] flex justify-center">
          <span className="animate-bounce rounded-xl bg-amber-400 px-4 py-2 font-display text-lg text-[#1e3a5f] shadow-lg">
            ✨ EXTRA LIFE! ✨
          </span>
        </div>
      )}

      {/* Ready overlay */}
      {phase === 'ready' && (
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-6">
          <div className="text-center">
            <h1 className="font-display text-4xl text-white [text-shadow:0_4px_0_rgba(0,0,0,0.25)] sm:text-5xl">
              FLAPPY BIRD
            </h1>
            <p className="mt-3 text-xs font-semibold uppercase tracking-[0.2em] text-white/90">
              unlimited · endless arcade
            </p>
          </div>
          <div className="rounded-2xl border border-white/60 bg-[rgba(30,58,95,0.55)] px-6 py-5 text-center text-white backdrop-blur-sm">
            <p className="font-display text-sm">TAP TO FLAP</p>
            <p className="mt-3 max-w-[52ch] text-xs leading-relaxed text-white/80">
              Tap, click, or press <b>Space</b> to flap. Dodge the pipes and fly as
              far as you can — it gets faster every pipe! Collect{' '}
              <CoinIcon className="inline-block h-4 w-4 align-[-2px]" /> coins:
              every 2 (then 5, then 8…) buys an <b>extra life</b>.
            </p>
          </div>
        </div>
      )}

      {/* Game over overlay — score, best, and both top-10 leaderboards */}
      {phase === 'gameover' && (
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center bg-[rgba(30,58,95,0.35)] p-3">
          <div className="w-[340px] max-w-[94vw] rounded-2xl border border-white/60 bg-white/95 p-4 text-center shadow-xl">
            <h2 className="font-display text-sm text-[#1e3a5f]">GAME OVER</h2>

            <div className="mt-3 grid grid-cols-2 gap-2">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-[#1e3a5f]/50">
                  Score
                </p>
                <p className="mt-1 font-display text-lg text-[#1e3a5f]">{score}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-[#1e3a5f]/50">
                  Best
                </p>
                <p className="mt-1 font-display text-lg text-amber-500">{best}</p>
              </div>
            </div>

            {isNewBest && (
              <p className="mt-2 animate-pulse rounded-full bg-amber-100 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-amber-600">
                🏆 New best!
              </p>
            )}

            {identity?.name && (
              <p className="mt-2 truncate text-[11px] font-semibold text-[#1e3a5f]/70">
                Playing as {identity.name}
              </p>
            )}

            <div className="mt-3 grid grid-cols-2 gap-2 text-left">
              <BoardTable
                title="🌍 Global top 10"
                entries={boards?.global ?? []}
                highlight={identity?.name ?? null}
                loading={boardsLoading && !boards}
              />
              <BoardTable
                title={monthLabel(boards?.month ?? monthKeyLocal(Date.now()))}
                entries={boards?.monthly ?? []}
                highlight={identity?.name ?? null}
                loading={boardsLoading && !boards}
              />
            </div>

            {boardsNote && (
              <p className="mt-2 text-[10px] font-medium text-[#1e3a5f]/60">{boardsNote}</p>
            )}

            <p className="mt-3 text-[11px] font-medium text-[#1e3a5f]/60">
              Tap or press Space to play again
            </p>
          </div>
        </div>
      )}

      <EtooLogo />

      {/* Controls hint */}
      <footer className="pointer-events-none absolute inset-x-0 bottom-4 flex justify-center">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[rgba(255,255,255,0.7)] px-3 py-1.5 text-[11px] font-medium text-[#1e3a5f]/80 backdrop-blur-sm">
          <kbd className="rounded border border-[#1e3a5f]/20 bg-white px-1.5 py-0.5 text-[10px] font-bold text-[#1e3a5f] shadow-sm">Space</kbd>
          or click / tap to flap
        </span>
      </footer>
    </div>
  );
}